import { Fragment } from 'react';
import { CandidateCard } from '@tempmee/react-blocks';
import { Column, Divider } from '@tempmee/react';
import { useParentOffice } from '~/hooks/useParentOffice';
import { useSelectedOffice } from '~/hooks/useSelectedOffice';
import { IJob, IMultipleCounters } from '~/models';
import { DetailsPanel } from '~/components/shared/DetailsPage';
import { toCandidateCardData, toCompanyMatch } from '../../shared/utils/candidateCardProps';

export interface ICandidateListPanelProps {
  /** Every counter offer on the shift, in the order the backend ranked them. */
  counters: readonly IMultipleCounters[];
  job: Partial<IJob> | null | undefined;
  /** Opens one candidate's own page. */
  onSelectCandidate: (candidateId: string) => void;
}

/**
 * The Confirm Candidates page's primary column: every professional who applied,
 * one `CandidateCard` per row.
 *
 * Ordering is the backend's — the candidate list endpoint already sorts by the
 * office's default (skills match, or local favourites on a general-dentistry
 * shift), and re-sorting here would silently override that ranking.
 *
 * The rules between rows are the design's, not the block's: `CandidateCard` draws
 * no chrome of its own so a list can space and separate its rows however the
 * surface needs.
 */
export const CandidateListPanel = ({
  counters,
  job,
  onSelectCandidate,
}: ICandidateListPanelProps) => {
  const offerOwner = useSelectedOffice(job?.offer_owner?.id);
  const { office: parentOffice } = useParentOffice();

  return (
    <DetailsPanel
      title="Candidates"
      data-testid="candidate-list-panel"
      data-cy="candidate-list-panel"
    >
      {/*
       * The 16px between rows is the container's gap rather than a margin on the
       * rule: dental-portal has no Tailwind build, so only the utilities the design
       * system's own stylesheet emits resolve — and `my-4` is not among them.
       */}
      <Column gap={4} className="w-full">
        {counters.map((counter, index) => {
          const companyMatch = toCompanyMatch(counter, { offerOwner, parentOffice });

          return (
            <Fragment key={counter.id}>
              {index > 0 ? <Divider data-testid="candidate-list-divider" /> : null}
              <CandidateCard
                {...toCandidateCardData({ counter, job, companyMatch })}
                onCardClick={() => onSelectCandidate(counter.candidate?.id)}
                data-testid={`candidate-card-${counter.candidate?.id}`}
                data-cy="candidate-card"
              />
            </Fragment>
          );
        })}
      </Column>
    </DetailsPanel>
  );
};
